<!--
  CWS-1781: the 15-column order from the PRD and the Orders Refund Flow design —
  Last visit · Order ID · License plate · Lane · Status · Wash type · Package name ·
  Order amount · Customer name · Payment mode · Payment status · Employee name ·
  Created · Discount · Source — plus the sticky Actions column.
  ORDER_SHIMMER_COLUMNS in orders.constants.ts mirrors this header; keep them in step.
-->
<div class="table-scroll-wrapper">
  <table class="orders-table">
    <thead>
      <tr>
        <th (click)="sortByDateTime.emit()" style="cursor: pointer">
          Last visit
          <img [src]="getSortIcon('dateTime')" alt="Sort" class="sort-icon" />
        </th>
        <th (click)="sortByOrderId.emit()" style="cursor: pointer">
          Order ID
          <img [src]="getSortIcon('orderId')" alt="Sort" class="sort-icon" />
        </th>
        <th>License plate</th>
        <th>Lane</th>
        <th>Status</th>
        <th>Wash type</th>
        <th>Package name</th>
        <th>Order amount</th>
        <th>Customer name</th>
        <th>Payment mode</th>
        <th>Payment status</th>
        <th>Employee name</th>
        <th>Created</th>
        <th>Discount</th>
        <th>Source</th>
        <th class="actions-header">Actions</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let order of orders" class="order-row">
        <td class="date-time">{{ order.dateTime || '-' }}</td>
        <td class="order-id">
          <button class="order-link" type="button" (click)="openOrderDetails.emit(order)">
            {{ order.orderId || '-' }}
          </button>
        </td>
        <td class="license-plate">{{ order.licensePlate || '-' }}</td>
        <td class="lane-cell">{{ order.laneName || '-' }}</td>
        <td class="status-cell">
          <span class="status-badge" [ngClass]="getStatusClass(order.status)">
            {{ order.status?.label || '-' }}
          </span>
        </td>
        <td>
          <div class="wash-type-cell">
            <span
              class="wash-type-badge"
              [ngClass]="getWashTypeClass(order.washType, order.washTypeValue)"
            >
              {{ order.washType || '-' }}
            </span>
            <!-- CWS-1125: a detailing / add-on sold with the wash is not in washType. -->
            <span
              *ngFor="let addOnLabel of order.addOnLabels"
              class="wash-type-badge"
              [ngClass]="getAddOnLabelClass(addOnLabel)"
            >
              {{ addOnLabel }}
            </span>
          </div>
        </td>
        <td class="package-name">
          {{ order.packageName || '-' }}
          <!-- CWS-1582 §8: one order per family plate; the primary's order says so. -->
          <span class="package-name__tags" *ngIf="order.isFamily">
            <span class="family-tag" data-testid="order-family-tag">{{ familyTagLabel }}</span>
            <span
              class="family-tag"
              data-testid="order-family-primary"
              *ngIf="order.isFamilyPrimary"
              >{{ familyPrimaryLabel }}</span
            >
          </span>
          <!-- CWS-1444: the name above is the plan the membership is on now;
               this says what the order was sold on, as the tunnel card does. -->
          <span
            class="package-name__sold-as"
            *ngIf="order.planChanged && order.soldPackageName"
            [title]="'Now on ' + order.packageName + ' (sold as ' + order.soldPackageName + ')'"
          >
            Sold as: {{ order.soldPackageName }}
          </span>
        </td>
        <td class="order-amount">{{ getOrderAmount(order) }}</td>
        <td class="customer-name">{{ order.customerName || '-' }}</td>
        <td class="payment-mode">{{ formatPaymentMethod(order.payment_method) || '-' }}</td>
        <!-- CWS-1777: derived server-side from everything refunded to date. -->
        <td class="payment-status-cell">
          <span class="payment-status-wrap">
            <span
              class="status-badge payment-status-badge"
              [ngClass]="getPaymentStatusClass(order)"
              [attr.data-payment-status]="getPaymentStatusBadge(order).value || null"
            >
              {{ getPaymentStatusBadge(order).label }}
            </span>
            <!-- CWS-1779: the amount behind a refunded badge, on hover -->
            <span
              class="payment-status-hint"
              *ngIf="getPaymentStatusHint(order) as hint"
              role="tooltip"
              >{{ hint }}</span
            >
          </span>
        </td>
        <td class="employee-name">{{ order.userName || '-' }}</td>
        <td class="created-at">{{ order.createdAt || '-' }}</td>
        <td class="discount">
          <ng-container *ngIf="order.discountName && order.discountId; else discountValueOnly">
            <a
              *ngIf="hasDiscountLink(order); else discountNamePlain"
              class="discount-value discount-name-link"
              [routerLink]="['/discount']"
              [queryParams]="{ discountId: order.discountId }"
            >
              {{ order.discountName }}
            </a>
            <ng-template #discountNamePlain>
              <span class="discount-value discount-name">{{ order.discountName }}</span>
            </ng-template>
            <span class="discount-separator"> - </span>
            <span class="discount-value">{{ order.discount || '-' }}</span>
          </ng-container>
          <ng-template #discountValueOnly>{{ order.discount || '-' }}</ng-template>
        </td>
        <td class="source">{{ getCapitalizedSource(order.source) || '-' }}</td>
        <td class="actions">
          <button
            class="action-button"
            type="button"
            *ngIf="canShowActions(order)"
            (click)="onToggleDropdown($event, order.id)"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 8.5C8.27614 8.5 8.5 8.27614 8.5 8C8.5 7.72386 8.27614 7.5 8 7.5C7.72386 7.5 7.5 7.72386 7.5 8C7.5 8.27614 7.72386 8.5 8 8.5Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M8 4.5C8.27614 4.5 8.5 4.27614 8.5 4C8.5 3.72386 8.27614 3.5 8 3.5C7.72386 3.5 7.5 3.72386 7.5 4C7.5 4.27614 7.72386 4.5 8 4.5Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M8 12.5C8.27614 12.5 8.5 12.2761 8.5 12C8.5 11.7239 8.27614 11.5 8 11.5C7.72386 11.5 7.5 11.7239 7.5 12C7.5 12.2761 7.72386 12.5 8 12.5Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </td>
      </tr>
    </tbody>
  </table>
</div>
